import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import retailSquare from './../../assets/images/retail-shapes-square.svg';
import retailCircle from './../../assets/images/retail-shapes-circle.svg';
import retailTriangle from './../../assets/images/retail-shapes-triangle.svg';
import './../retail.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function RetailPlanning() {

  const squareRef = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(squareRef.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="retail-planning">

        <p id="s1" className="intro" data-aos="fade-up">Explore the stats to see how businesses in the retail industry plan and manage direct mail.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail Expertise</h3>
                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="59" aria-hidden="true">0</span><span class="screen-reader-text">59</span>%</span>
                </span>

                <p>Advanced knowledge</p>
                <p>Retail companies reported having less expertise in direct mail than businesses in other industries, with the exception of health care businesses.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>To get a better handle on the basics of direct mail, explore a <a href="/../dm-101-tutorial/">step-by-step tutorial</a> covering everything from data and mailing lists to ROI and postage.</p>
              </div>
            </div>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>In-House Mail <br />Management</h3>
                <div data-aos data-aos-id="percentage">
                  <span data-aos className='percentage-value large highlighter '><span class="percentage" data-to="57" aria-hidden="true">0</span><span class="screen-reader-text">57</span>%</span>
                </div>
                <p>Performance measurement</p>
                <p><span className='printing-percentage'>55%</span> Sorting, assembling and mailing</p>
                <p>Retail companies are the least likely to handle performance measurement and sorting, assembling and mailing with outside agencies. Retail companies handle most direct mail tasks primarily in-house.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>If your team doesn’t have the experience to handle certain aspects of direct mail in-house, specialized experts can help. Do some research and prepare some basic questions before you begin the search. Is the company known for finishing jobs on time? What do customers like or dislike about them? Do they have references?</p>
              </div>
            </div>
          </div>

          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Management of Direct Mail</h3>
                <p className="pull-quote">Across industries, businesses usually have either <span data-aos data-aos-once="true" className="highlighter">in-house or agency-shared responsibility</span> for managing direct mail. In general, companies rarely depend solely on outside agencies.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p> <a href="/../questions-to-ask-before-choosing-a-mail-service-provider/">Specialized partners</a> can help you manage some—or all—elements of direct mail campaigns. Mail service providers (MSPs), advertising agencies and printers can help with various aspects, including writing and designing your piece, making sure it meets USPS requirements, printing and presorting, and more.</p>
              </div>
            </div>

            <div className='personalized-box secondary'>
              <h3>Retail companies carry out most direct mail tasks in-house.</h3>
              <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                <div className='triangle triangle-1'>
                  <img ref={triangle1Ref} src={retailTriangle} alt="" />
                </div>
                <div className='square'>
                  <img ref={squareRef} src={retailSquare} alt="" />
                </div>
                <div className='triangle triangle-2'>
                  <img ref={triangle2Ref} src={retailTriangle} alt="" />
                </div>
                <div className='circle'>
                  <img src={retailCircle} alt="" />
                </div>
              </div>
            </div>

            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Every Door Direct Mail<sup>&reg;</sup> Use</h3>
                <div className='progress-bar-area' data-aos data-aos-id="bar-anim">
                  <div className='progressbar-item'>
                    <span className='label'>Retail</span>
                    <div className='progress-bar size-small'>
                      <div className='progress' data-fill="49"><span class="percentage" data-to="49" aria-hidden="true">0</span><span class="screen-reader-text">49</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Consumer packaged goods (CPG)</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="49"><span class="percentage" data-to="49" aria-hidden="true">0</span><span class="screen-reader-text">49</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Insurance</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="46"><span class="percentage" data-to="46" aria-hidden="true">0</span><span class="screen-reader-text">46</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Financial</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="44"><span class="percentage" data-to="44" aria-hidden="true">0</span><span class="screen-reader-text">44</span>%</div>
                    </div>
                  </div>
                  <div className='progressbar-item'>
                    <span className='label'>Health</span>
                    <div className='progress-bar size-small'>
                      <div className='progress fill-light' data-fill="40"><span class="percentage" data-to="40" aria-hidden="true">0</span><span class="screen-reader-text">40</span>%</div>
                    </div>
                  </div>
                </div>
                <p>Along with CPG companies, retail companies are more likely than businesses in other industries to use the Every Door Direct Mail (EDDM<sup>&reg;</sup>) feature.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p id="s2">The Every Door Direct Mail service provides a simple, affordable way to gain new customers—and engage existing ones—within specific regions, across specific demographics.<a href="#reference-2" className="cm-article-content__reference" aria-label="Footnote 2" title="Footnote 2"><sup aria-hidden="true">[2]</sup></a> <a href="/../eddm-tool-guide/">Explore the EDDM video tutorial</a> to learn more.</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
